import { Draft } from "immer";
import { isBlank } from "utils/string";
import { NOTIFICATION_LIMITS } from "@dashboard-v3/shared";

import {
  DoneNotification,
  EmailExpiration,
  Reminder,
  SenderNotification,
  Supervisor,
  SupervisorNotification,
} from "@dashboard-v3/api";
import { State } from "../../types";
import { getApprovalSteps } from "pages/Rules/Form/context/ruleSelectors";

export default function validate(draft: Draft<State>) {
  const {
    supervisors,
    expiration,
    senderNotification,
    doneNotification,
    supervisorNotification,
    reminderSenderNotification,
    reminder,
  } = getApprovalSteps(draft.rule);

  if (!supervisors.length) {
    draft.steps.approvalSteps.valid = false;
    return;
  }

  const errors = [];

  const supervisorsValidation = validateSupervisors(supervisors);
  const expirationValidation = validateExpiration(expiration);
  const reminderPeriodValidation = validateReminderPeriod(reminder, expiration);
  const senderValidation = validateSettings("SENDER", senderNotification);
  const doneValidation = validateSettings("COMPLETION", doneNotification);
  const supervisorValidation = validateSettings(
    "SUPERVISOR",
    supervisorNotification
  );
  const reminderValidation = validateSettings(
    "REMINDER",
    reminderSenderNotification
  );

  if (!expirationValidation.isValid) {
    errors.push(expirationValidation.error);
  }

  if (!senderValidation.isValid) {
    errors.push(...senderValidation.errors);
  }

  if (!doneValidation.isValid) {
    errors.push(...doneValidation.errors);
  }

  if (!supervisorValidation.isValid) {
    errors.push(...supervisorValidation.errors);
  }

  if (!reminderValidation.isValid) {
    errors.push(...reminderValidation.errors);
  }

  if (!reminderPeriodValidation.isValid) {
    errors.push(reminderPeriodValidation.error);
  }

  draft.steps.approvalSteps.error = errors;
  draft.steps.approvalSteps.valid =
    supervisorsValidation && errors.length === 0;
}

function validateSupervisors(supervisors: Supervisor[]) {
  return !supervisors.some(s => isBlank(s.entity) || isBlank(s.type));
}

function getMaxLength(key: string): number | null {
  if (key.endsWith("Label")) return NOTIFICATION_LIMITS.CTA_LABEL;
  if (key === "title") return NOTIFICATION_LIMITS.TITLE;
  if (key === "subject") return NOTIFICATION_LIMITS.EMAIL_SUBJECT;
  if (key.toLowerCase().includes("bodytext"))
    return NOTIFICATION_LIMITS.BODY_TEXT;
  return null;
}

function validateSettings(
  type: "SUPERVISOR" | "SENDER" | "REMINDER" | "COMPLETION",
  notification: SenderNotification | SupervisorNotification | DoneNotification
) {
  const errors: string[] = [];

  for (const key in notification) {
    const value = notification[key];

    if (typeof value !== "string") continue;

    if (isBlank(value)) {
      if (!key.endsWith("Label")) {
        errors.push(`${type}_${key.toUpperCase()}_REQUIRED`);
        continue;
      }

      const linkKey = key.replace("Label", "");
      const isLinkEnabled = notification[linkKey] === true;

      if (isLinkEnabled) {
        errors.push(`${type}_${linkKey.toUpperCase()}_FIELD_ERROR`);
      }
      continue;
    }

    const maxLength = getMaxLength(key);
    if (maxLength && value.length > maxLength) {
      errors.push(`${type}_${key.toUpperCase()}_MAX_LENGTH`);
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

function validateExpiration(expiration: EmailExpiration) {
  const { type } = expiration;
  const value = Number(expiration.value);

  switch (type) {
    case "HOURS":
      if (value < 1) return { isValid: false, error: "EXCEEDS_VALUE_MIN" };
      if (value > 24) return { isValid: false, error: "EXCEEDS_UNIT_MAX" };
      break;
    case "DAYS":
      if (value < 1) return { isValid: false, error: "EXCEEDS_UNIT_MIN" };
      if (value > 30) return { isValid: false, error: "EXCEEDS_VALUE_MAX" };
      break;
  }

  return { isValid: true, error: "" };
}

export function validateReminderPeriod(
  reminder: Reminder,
  expiration: EmailExpiration
) {
  if (!reminder || !reminder.value) {
    return { isValid: true };
  }

  // Convert both to hours for easier comparison
  const expirationHours =
    expiration.type === "HOURS" ? expiration.value : expiration.value * 24;

  const reminderHours =
    reminder.type === "HOURS" ? reminder.value : reminder.value * 24;

  // Reminder cannot be greater than expiration
  if (reminderHours > expirationHours) {
    return { isValid: false, error: "REMINDER_INTERVAL_EXCEEDED" };
  }

  let minIntervalHours: number;

  if (expirationHours < 24) {
    minIntervalHours = 2;
  } else if (expirationHours >= 24 && expirationHours <= 72) {
    minIntervalHours = 6;
  } else if (expirationHours >= 72 && expirationHours <= 168) {
    minIntervalHours = 12;
  } else {
    minIntervalHours = 48;
  }

  if (reminderHours < minIntervalHours) {
    return { isValid: false, error: "REMINDER_INTERVAL_MIN_ERROR" };
  }

  return { isValid: true };
}
